import { useEffect, useMemo, useRef, useState } from "react";
import { useReportViewStore } from "../../../store/report/useReportViewStore";
import { useReportsMetadataQuery } from "../../../hooks/reports/queries/useGroupQueries";
import { ReportMetadataGroup } from "../../../models/pageModels/ReportLibrary";

import SidePanelToolbar from "./SidePanelToolbar";
import SidePanelItem from "./items/SidePanelItem";
import SidePanelGroup from "./items/SidePanelGroup";

import Loader from "../../UI/Loader";
import "./ReportsSidePanel.css";

const ReportsSidePanel = () => {
  const { data: reportsData, isLoading } = useReportsMetadataQuery();
  const reports = useMemo(() => reportsData ?? { bookmarkedReports: [], recentReports: [] }, [reportsData]);
  const isSidePanelOpen = useReportViewStore((s) => s.isSidePanelOpen);
  const cleanupExpandedGroups = useReportViewStore((s) => s.cleanupExpandedGroups);

  useEffect(() => {
    if (!reportsData) return;
    const validGroupIds = reportsData.bookmarkedReports
      .filter((item): item is ReportMetadataGroup => item.type === "group")
      .map((g) => g.id);
    cleanupExpandedGroups(validGroupIds);
  }, [reportsData, cleanupExpandedGroups]);

  const [panelWidth, setPanelWidth] = useState(300);
  const isResizing = useRef(false);
  const panelRef = useRef<HTMLElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    isResizing.current = true;
    panelRef.current?.classList.add("resizing");
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current || !panelRef.current) return;
      const newWidth = e.clientX - panelRef.current.getBoundingClientRect().left;
      if (newWidth >= 300 && newWidth <= 500) setPanelWidth(newWidth);
    };
    const handleMouseUp = () => {
      isResizing.current = false;
      panelRef.current?.classList.remove("resizing");
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const reportCount = useMemo(() =>
    reports.bookmarkedReports.flatMap((item) =>
      item.type === "group" ? item.reports : [item]
    ).length,
  [reports.bookmarkedReports]);

  return (
    <aside
      ref={panelRef}
      className={`reports-side-panel ${isSidePanelOpen ? "expanded" : "collapsed"}`}
      style={isSidePanelOpen ? { width: `${panelWidth}px` } : undefined}
    >
      {isSidePanelOpen && <div className="resize-handle" onMouseDown={handleMouseDown} />}
      
      <SidePanelToolbar />

      <div className="side-panel-content">
        {/* --- MY REPORTS SECTION --- */}
        <div className="side-panel-section">
          <div className="section-header"><h2>My Reports</h2></div>
          
          {isLoading ? (
             <div className="loader-container"><Loader /></div>
          ) : reportCount === 0 ? (
             <p className="text-center">No Saved reports.</p>
          ) : (
            <ul className="side-panel-list">
              {reports.bookmarkedReports.map((item, index) => {
                const showDivider = item.type !== "group" && reports.bookmarkedReports[index - 1]?.type === "group";
                return item.type === "group" ? (
                  <SidePanelGroup key={item.id} id={item.id} groupName={item.name} reports={item.reports} />
                ) : (
                  <div key={item.id}>
                    {showDivider && <hr className="side-panel-divider" />}
                    <SidePanelItem report={item} isSelectable={true} />
                  </div>
                );
              })}
            </ul>
          )}
        </div>

        {/* --- RECENT REPORTS SECTION --- */}
        <div className="side-panel-section">
          <div className="section-header"><h2>Recent Reports</h2></div>
          
          {isLoading ? (
             <div className="loader-container"><Loader /></div>
          ) : reports.recentReports.length === 0 ? (
             <p className="text-center">No Recent reports.</p>
          ) : (
            <ul className="side-panel-list">
              {reports.recentReports.map((report) => (
                <SidePanelItem key={report.id} report={report} />
              ))}
            </ul>
          )}
        </div>

      </div>
    </aside>
  );
};

export default ReportsSidePanel;
